import {
  AlignmentType, BorderStyle, Document, Footer, Header, HeadingLevel, ImageRun, LevelFormat,
  Packer, PageNumber, Paragraph, PositionalTab, PositionalTabAlignment, PositionalTabLeader,
  PositionalTabRelativeTo, ShadingType, Table, TableBorders, TableCell, TableRow, TextRun, WidthType,
} from 'docx';
import type {
  ClientWeeklyReport, InternalWeeklyReport, WeeklyReportBase, WeeklyReportDay, WeeklyReportExtraSection, WeeklyReportSectionKey,
} from '@/lib/apiClient';
import type { EmbeddedPhoto } from './reportPhotos';
import {
  crewTotals, customerTeamRows, dayHasExportContent, dayHeading, daysByWeek, detailRows, equipmentLine, exportableLog,
  extraSectionsAfter, generatedLabel, lookaheadLines, MANPOWER_FOOTNOTE, manpowerRows, quantityLine, sectionLines, sectionTables, teamWithCarry,
  TRACKER_LINE_STYLE, typedEquipment, weatherLine, weekOf, type CarriedSection,
} from './weeklyReportModel';

/**
 * The weekly project report as a Word document (#576 internal, #579 client).
 *
 * Laid out like the PM's own template: header, Project Details, both teams,
 * then the narrative sections as labeled headings with an empty bullet to
 * type into. Each lookback week of the progress summary carries that week's
 * daily logs by date, with their photos. The PM writes the narrative in Word
 * and reviews the file before sending it.
 *
 * Once a previous week's finalized report has been uploaded, what the PM
 * typed there fills this draft (`r.carryForward`): the customer team, our
 * team's blank rows and missing phones, Project Details the Tracker has blank,
 * the look-ahead by date, Materials, Survey, Safety and Logistics with their
 * tables, Manpower notes (and counts for weeks the schedule has none), typed
 * Equipment lines, and any section the PM added, in its place. The Tracker's
 * own data wins wherever it has a value. `weeklyReportRead.ts` reads it back
 * by these same headings, and tells the Tracker's values from typed ones:
 * Project Details by label (`trackerDetails`, which this file writes from)
 * and Manpower rows by the DATE in their Week Of cell, both compared with the
 * week's report; Equipment lines and the photo grid's text by the paragraph
 * style this file gives every such line it writes (TRACKER_LINE_STYLE). Only
 * a file exported before that style falls back to an equipment item's name
 * (`isTrackerEquipmentLine`). A heading, a Week Of date or that style changed
 * here must stay readable there. That upload is the only way anything typed
 * in Word reaches the Tracker; the report itself stays generate-and-edit, the
 * model #576 chose.
 *
 * ⚠️ TWO ENTRY POINTS, TWO TYPES. `buildClientReportDocx` takes only a
 * `ClientWeeklyReport`, which only the client endpoint returns, already cut to
 * its allowlist on the server. The Manpower and Equipment sections are built
 * only inside `buildInternalReportDocx`, from fields the client type does not
 * have, so the compiler refuses to put them in a client copy.
 */

const GREEN = '027835';
const GRID = 'D9D9D7';
const MUTED = '767676';
// US Letter, 0.75" margins: 12240 − 2 × 1080 = 10080 DXA of content.
const PAGE = { width: 12240, height: 15840 };
const MARGIN = 1080;
const CONTENT = PAGE.width - 2 * MARGIN;
const BULLETS = 'wr-bullets';

/** The template's customer-team roles. The Tracker stores no customer contacts, so the PM fills the rows. */
export const CUSTOMER_TEAM_ROLES = ['Project Manager', 'Construction Manager', 'Project Controls Engineer'];

export const FOOTER_NOTE = {
  internal: 'Confidential – Internal Use Only',
  // #579: keep the confidentiality line on the client copy too. It is
  // confidential between the company and its client, not internal.
  client: 'Confidential',
} as const;

type Block = Paragraph | Table;

// ─── Primitives ──────────────────────────────────────────────────────────────

/**
 * A section title as the template sets it: dark bold capitals, no fill (the
 * template's only green is its table header rows), kept with what follows.
 * `newPage` mirrors the template's own page breaks, before Weekly Progress
 * Summary and before Manpower.
 */
function sectionHeading(text: string, newPage = false): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    keepNext: true,
    pageBreakBefore: newPage,
    spacing: { before: 280, after: 100 },
    children: runs(text.toUpperCase(), { bold: true }),
  });
}

type RunStyle = { bold?: boolean; italics?: boolean; color?: string; size?: number };

/**
 * Text typed into the Tracker, made safe and faithful for Word.
 *
 * ⚠️ XML 1.0 forbids most control characters, and the `docx` package writes
 * them through as-is, so one vertical tab pasted into a daily log (Word's own
 * line break, when copied out of Word) makes the whole file unreadable. They
 * are dropped here; tab and line breaks are kept.
 *
 * A line break becomes a real one: a `\n` inside a TextRun does not break the
 * line in Word, so a three-line note would print as one run-on line.
 */
// U+FFFE and U+FFFF are forbidden in XML 1.0 too. Built from their codes so
// the source holds no invisible characters (an editor can turn an escape in a
// regex literal into the raw character, which no reviewer can see).
const XML_NONCHARACTERS = new RegExp(`[${String.fromCharCode(0xfffe, 0xffff)}]`, 'g');

export function cleanText(text: string): string {
  return text
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(XML_NONCHARACTERS, '')
    .replace(/\r\n?/g, '\n');
}

export function runs(text: string, style: RunStyle = {}): TextRun[] {
  return cleanText(text).split('\n').map((part, i) => new TextRun({ text: part, ...style, ...(i > 0 ? { break: 1 } : {}) }));
}

function subHeading(text: string): Paragraph {
  return new Paragraph({ keepNext: true, spacing: { before: 120, after: 40 }, children: runs(text, { bold: true }) });
}

function bullet(text = ''): Paragraph {
  return new Paragraph({ numbering: { reference: BULLETS, level: 0 }, children: runs(text) });
}

/** A bullet the Tracker wrote from its own data, marked so the reader never takes it for typed (TRACKER_LINE_STYLE). */
function trackerBullet(text: string): Paragraph {
  return new Paragraph({ style: TRACKER_LINE_STYLE, numbering: { reference: BULLETS, level: 0 }, children: runs(text) });
}

function line(text: string, style: RunStyle = {}): Paragraph {
  return new Paragraph({ spacing: { after: 40 }, children: runs(text, style) });
}

function labeled(label: string, value: string): Paragraph {
  return new Paragraph({
    spacing: { after: 40 },
    children: [new TextRun({ text: `${label}: `, bold: true }), ...runs(value)],
  });
}

const cellBorders = {
  top: { style: BorderStyle.SINGLE, size: 4, color: GRID },
  bottom: { style: BorderStyle.SINGLE, size: 4, color: GRID },
  left: { style: BorderStyle.SINGLE, size: 4, color: GRID },
  right: { style: BorderStyle.SINGLE, size: 4, color: GRID },
};

function cell(text: string, width: number, header = false): TableCell {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    borders: cellBorders,
    shading: header ? { type: ShadingType.CLEAR, fill: GREEN, color: 'auto' } : undefined,
    margins: { top: 40, bottom: 40, left: 100, right: 100 },
    children: [new Paragraph({ children: runs(text, { bold: header, color: header ? 'FFFFFF' : undefined }) })],
  });
}

/**
 * A table the PM made, carried from last week as typed: its own column count,
 * in equal widths, and the template's green header row only when its first
 * row was one.
 */
function freeTable(rows: string[][], header: boolean): Table {
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const width = Math.floor(CONTENT / cols);
  const widths = Array.from({ length: cols }, (_, i) => (i === cols - 1 ? CONTENT - width * (cols - 1) : width));
  const cells = (r: string[], head: boolean) => widths.map((w, i) => cell(r[i] ?? '', w, head));
  return new Table({
    width: { size: CONTENT, type: WidthType.DXA },
    columnWidths: widths,
    rows: rows.map((r, i) => new TableRow({ tableHeader: header && i === 0, children: cells(r, header && i === 0) })),
  });
}

/** A green-headed grid table, like the template's. Column widths in DXA must sum to CONTENT. */
function grid(headers: string[], rows: string[][], widths: number[]): Table {
  return new Table({
    width: { size: CONTENT, type: WidthType.DXA },
    columnWidths: widths,
    rows: [
      new TableRow({ tableHeader: true, children: headers.map((h, i) => cell(h, widths[i], true)) }),
      ...rows.map((r) => new TableRow({ children: r.map((v, i) => cell(v, widths[i])) })),
    ],
  });
}

// ─── Shared sections (both versions) ─────────────────────────────────────────

/** Project Details: the Tracker's values, its blanks filled from last week's report, and rows the PM added. */
function projectDetails(r: WeeklyReportBase): Block[] {
  return [
    sectionHeading('Project Details'),
    grid(['Field', 'Value'], detailRows(r.project, r.carryForward).map((d) => [d.label, d.value]), [3000, CONTENT - 3000]),
  ];
}

const TEAM_WIDTHS = [2600, 2500, 1900, CONTENT - 2600 - 2500 - 1900];

/** The customer's team: from the previous finalized report when there is one (the Tracker stores no customer contacts). */
function customerTeam(r: WeeklyReportBase): Block[] {
  const customer = r.project.client?.trim();
  const carried = customerTeamRows(r.carryForward);
  return [
    sectionHeading(customer ? `${customer} Team` : 'Customer Team'),
    grid(['Role', 'Name', 'Phone', 'Email'],
      carried
        ? carried.map((p) => [p.role, p.name ?? '', p.phone ?? '', p.email ?? ''])
        : CUSTOMER_TEAM_ROLES.map((role) => [role, '', '', '']),
      TEAM_WIDTHS),
  ];
}

/** Ours: the job's assignments, with its blank rows and missing phones filled from that report. */
function ewTeam(r: WeeklyReportBase): Block[] {
  return [
    sectionHeading('East & Westbrook Construction Team'),
    grid(['Role', 'Name', 'Phone', 'Email'],
      teamWithCarry(r.team, r.carryForward).map((t) => [t.role, t.name ?? '', t.phone ?? '', t.email ?? '']), TEAM_WIDTHS),
  ];
}

/** The bullets to type into: last report's lines when it had some, or one empty bullet. */
function bullets(lines: string[]): Paragraph[] {
  return lines.length ? lines.map((l) => bullet(l)) : [bullet()];
}

/** A narrative section carried whole from the previous report (Materials, Survey, Safety, Logistics), its tables after its lines. */
function narrative(r: WeeklyReportBase, title: string, section: CarriedSection): Block[] {
  return [
    sectionHeading(title),
    ...bullets(sectionLines(r.carryForward, section)),
    ...sectionTables(r.carryForward, section).map((tb) => freeTable(tb.rows, tb.header)),
  ];
}

/** Sections the PM added last week that came right after `after`, whole, in their place. */
function extras(r: WeeklyReportBase, after: WeeklyReportSectionKey | null): Block[] {
  return extraSectionsAfter(r.carryForward, after).flatMap((s: WeeklyReportExtraSection) => [
    ...(s.title ? [sectionHeading(s.title)] : []),
    ...(s.blocks.length
      ? s.blocks.map((b) => (b.type === 'table'
        ? freeTable(b.rows, b.header)
        : b.style === 'heading' ? subHeading(b.text) : b.style === 'bullet' ? bullet(b.text) : line(b.text)))
      : [bullet()]),
  ]);
}

/**
 * The three-week look-ahead, a subheading per week. Last report's lines land
 * on the SAME dates, so its 2nd and 3rd weeks fill this report's 1st and 2nd.
 */
function lookahead(r: WeeklyReportBase): Block[] {
  return [
    sectionHeading('Three-Week Look Ahead'),
    ...r.window.lookahead.flatMap((ws) => [subHeading(weekOf(ws)), ...bullets(lookaheadLines(r.carryForward, ws))]),
  ];
}

// ─── Internal-only sections ──────────────────────────────────────────────────

/** Manpower: the Tracker's counts, last week's notes on the same weeks, and rows the PM added. */
function manpower(r: InternalWeeklyReport): Block[] {
  const rows = manpowerRows(r).map((m) => [m.label, m.headcount, [m.trackerNote, m.typedNote].filter(Boolean).join(' · ')]);
  return [
    sectionHeading('Manpower', true),
    grid(['Week Of', 'Headcount', 'Notes'], rows, [2800, 1600, CONTENT - 4400]),
    // Marked: anything else under the table is the PM's, and carries.
    new Paragraph({ style: TRACKER_LINE_STYLE, spacing: { after: 40 }, children: runs(MANPOWER_FOOTNOTE, { italics: true, color: MUTED, size: 18 }) }),
  ];
}

/** Equipment: the job's own items, then the lines the PM typed last week, then a bullet to type into. */
function equipment(r: InternalWeeklyReport): Block[] {
  return [
    sectionHeading('Equipment'),
    ...r.equipment.map((e) => trackerBullet(equipmentLine(e))),
    ...typedEquipment(r.carryForward).map((l) => bullet(l)),
    bullet(),
  ];
}

// ─── Daily logs and photos ───────────────────────────────────────────────────

/** Two photos to a row, each at most 3.1" wide, aspect kept. */
const PHOTO_MAX_W = 300;
const PHOTO_MAX_H = 260;

function photoCell(webUrl: string, fileName: string | null, caption: string, photos: Map<string, EmbeddedPhoto>, width: number): TableCell {
  const img = photos.get(webUrl);
  const children: Paragraph[] = [];
  if (img) {
    const scale = Math.min(PHOTO_MAX_W / img.width, PHOTO_MAX_H / img.height, 1);
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new ImageRun({
        type: img.type,
        data: img.data,
        transformation: { width: Math.round(img.width * scale), height: Math.round(img.height * scale) },
        // Cleaned too: alt text is XML like any other, and one control character
        // in a caption corrupts the file just as surely here.
        altText: { name: cleanText(fileName ?? 'Site photo'), description: cleanText(caption || 'Site photo'), title: cleanText(fileName ?? 'Site photo') },
      })],
    }));
  } else {
    // Named, not dropped: the PM reviewing the file sees what's missing.
    children.push(new Paragraph({
      style: TRACKER_LINE_STYLE,
      spacing: { after: 40 },
      children: runs(`Photo not available: ${fileName ?? 'unnamed'}`, { italics: true, color: MUTED }),
    }));
  }
  // Marked, like every line of the grid: the reader tells this grid from a
  // table the PM made with a picture in it by its text being all the Tracker's.
  if (caption) children.push(new Paragraph({ style: TRACKER_LINE_STYLE, alignment: AlignmentType.CENTER, children: runs(caption, { size: 18, color: MUTED }) }));
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    borders: TableBorders.NONE,
    margins: { top: 60, bottom: 60, left: 60, right: 60 },
    children,
  });
}

function photoGrid(day: WeeklyReportDay, photos: Map<string, EmbeddedPhoto>): Block[] {
  const items = day.photoPosts.flatMap((post) => post.photos.map((ph) => ({
    webUrl: ph.webUrl,
    fileName: ph.fileName,
    caption: [post.caption, post.author].filter((s) => s && s.trim()).join(' — '),
  })));
  if (!items.length) return [];
  const half = CONTENT / 2;
  const rows: TableRow[] = [];
  for (let i = 0; i < items.length; i += 2) {
    const pair = items.slice(i, i + 2);
    rows.push(new TableRow({
      cantSplit: true,
      children: [
        photoCell(pair[0].webUrl, pair[0].fileName, pair[0].caption, photos, half),
        pair[1]
          ? photoCell(pair[1].webUrl, pair[1].fileName, pair[1].caption, photos, half)
          : new TableCell({ width: { size: half, type: WidthType.DXA }, borders: TableBorders.NONE, children: [new Paragraph('')] }),
      ],
    }));
  }
  return [
    subHeading('Photos'),
    new Table({ width: { size: CONTENT, type: WidthType.DXA }, columnWidths: [half, half], borders: TableBorders.NONE, rows }),
  ];
}

function dayBlocks(day: WeeklyReportDay, photos: Map<string, EmbeddedPhoto>): Block[] {
  const out: Block[] = [new Paragraph({
    heading: HeadingLevel.HEADING_3,
    // Kept with the log below it, so a day's heading never ends a page alone.
    keepNext: true,
    spacing: { before: 280, after: 60 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: GRID, space: 2 } },
    children: [new TextRun({ text: dayHeading(day.date), bold: true })],
  })];

  const log = exportableLog(day);
  if (log) {
    const weather = weatherLine(log.weather);
    if (weather) out.push(labeled('Weather', weather));
    if (log.author) out.push(labeled('Submitted by', log.author));
    if (log.crews.length) {
      const t = crewTotals(log);
      out.push(subHeading(`Crews · ${t.workers} workers · ${t.manhours.toLocaleString('en-US', { maximumFractionDigits: 1 })} man-hours`));
      out.push(grid(['Company', 'Trade', 'Workers', 'Hours'],
        log.crews.map((c) => [c.company ?? '', c.trade ?? '', c.workers == null ? '' : String(c.workers), c.hours == null ? '' : String(c.hours)]),
        [3600, 3080, 1700, 1700]));
    }
    const work = log.work.filter((w) => (w.area ?? '').trim() || (w.narrative ?? '').trim());
    if (work.length) {
      out.push(subHeading('Work performed'));
      for (const w of work) out.push(bullet([w.area, w.narrative].filter((s) => s && s.trim()).join(': ')));
    }
    const qty = log.quantities.map(quantityLine).filter(Boolean);
    if (qty.length) {
      out.push(subHeading('Quantities installed'));
      for (const q of qty) out.push(bullet(q));
    }
    if (log.workSummary?.trim()) out.push(labeled('Summary', log.workSummary.trim()));
    if (log.delays?.trim()) out.push(labeled('Delays', log.delays.trim()));
    if (log.safetyNotes?.trim()) out.push(labeled('Safety', log.safetyNotes.trim()));
  } else {
    out.push(line('No submitted daily log for this day.', { italics: true, color: MUTED }));
  }
  out.push(...photoGrid(day, photos));
  return out;
}

/**
 * Weekly Progress Summary: each lookback week's heading, an empty bullet for
 * the PM's summary, then that week's daily logs by date with their photos, so
 * the record sits under the summary it supports (#576). A new page, as in the
 * template: page one is the details and the teams.
 */
function progressSummary(r: WeeklyReportBase, photos: Map<string, EmbeddedPhoto>): Block[] {
  return [
    sectionHeading('Weekly Progress Summary', true),
    ...daysByWeek(r).flatMap(({ weekStart, days }) => [
      subHeading(weekOf(weekStart)),
      bullet(),
      ...days.filter(dayHasExportContent).flatMap((d) => dayBlocks(d, photos)),
    ]),
  ];
}

// ─── The document ────────────────────────────────────────────────────────────

/** "<job number> <title>", one line, cleaned for Word (it goes in the header, footer and file properties). */
function jobLine(r: WeeklyReportBase): string {
  return cleanText([r.project.jobNumber, r.project.title].filter((s) => s && s.trim()).join(' ')).replace(/\n/g, ' ');
}

function header(r: WeeklyReportBase, logo: EmbeddedPhoto | null): Header {
  const right = (text: string, opts: { bold?: boolean; color?: string } = {}) =>
    new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text, size: 20, ...opts })] });
  const logoCell = new TableCell({
    width: { size: 4000, type: WidthType.DXA },
    borders: TableBorders.NONE,
    children: [new Paragraph({
      children: logo
        ? [new ImageRun({ type: logo.type, data: logo.data, transformation: { width: 210, height: Math.round(210 * logo.height / logo.width) }, altText: { name: 'East & Westbrook Construction', description: 'East & Westbrook Construction logo', title: 'East & Westbrook Construction' } })]
        : [new TextRun({ text: 'EAST & WESTBROOK CONSTRUCTION', bold: true, color: GREEN })],
    })],
  });
  const textCell = new TableCell({
    width: { size: CONTENT - 4000, type: WidthType.DXA },
    borders: TableBorders.NONE,
    children: [
      right('Weekly Project Report', { bold: true, color: GREEN }),
      right(jobLine(r)),
      right(generatedLabel(r.generatedAt), { color: MUTED }),
    ],
  });
  return new Header({
    children: [
      new Table({ width: { size: CONTENT, type: WidthType.DXA }, columnWidths: [4000, CONTENT - 4000], borders: TableBorders.NONE, rows: [new TableRow({ children: [logoCell, textCell] })] }),
      new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 12, color: GREEN, space: 4 } }, children: [] }),
    ],
  });
}

function footer(r: WeeklyReportBase, note: string): Footer {
  return new Footer({
    children: [
      new Paragraph({
        border: { top: { style: BorderStyle.SINGLE, size: 4, color: GRID, space: 4 } },
        children: [
          new TextRun({ text: jobLine(r), size: 16, color: MUTED }),
          new TextRun({
            size: 16,
            color: MUTED,
            children: [
              new PositionalTab({ alignment: PositionalTabAlignment.RIGHT, relativeTo: PositionalTabRelativeTo.MARGIN, leader: PositionalTabLeader.NONE }),
              'Page ', PageNumber.CURRENT, ' of ', PageNumber.TOTAL_PAGES,
            ],
          }),
        ],
      }),
      new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: note, size: 16, italics: true, color: MUTED })] }),
    ],
  });
}

function assemble(r: WeeklyReportBase, note: string, body: Block[], logo: EmbeddedPhoto | null): Document {
  return new Document({
    creator: 'East & Westbrook — Project Tracker',
    title: `Weekly Project Report — ${jobLine(r)}`,
    styles: {
      default: {
        document: { run: { font: 'Arial', size: 20 } },
        heading2: { run: { font: 'Arial', size: 22, bold: true, color: '363636' } },
        heading3: { run: { font: 'Arial', size: 21, bold: true, color: '363636' } },
      },
      // Looks like any line; only the reader cares. Enter after one starts a Normal line.
      paragraphStyles: [{ id: TRACKER_LINE_STYLE, name: 'Tracker Line', basedOn: 'Normal', next: 'Normal', quickFormat: false }],
    },
    numbering: {
      config: [{
        reference: BULLETS,
        levels: [{ level: 0, format: LevelFormat.BULLET, text: '•', alignment: AlignmentType.LEFT, style: { paragraph: { indent: { left: 360, hanging: 260 } } } }],
      }],
    },
    sections: [{
      properties: { page: { size: PAGE, margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN } } },
      headers: { default: header(r, logo) },
      footers: { default: footer(r, note) },
      children: body,
    }],
  });
}

/** The body both versions share, in the template's order, with the internal-only sections slotted in by the caller. */
function body(
  r: WeeklyReportBase,
  photos: Map<string, EmbeddedPhoto>,
  internalOnly: { manpower?: Block[]; equipment?: Block[] },
): Block[] {
  // Each template section, then any the PM added after it last week.
  return [
    ...extras(r, null),
    ...projectDetails(r), ...extras(r, 'details'),
    ...customerTeam(r), ...extras(r, 'customerTeam'),
    ...ewTeam(r), ...extras(r, 'ewTeam'),
    ...progressSummary(r, photos), ...extras(r, 'progress'),
    ...lookahead(r), ...extras(r, 'lookahead'),
    ...narrative(r, 'Materials', 'materials'), ...extras(r, 'materials'),
    ...(internalOnly.manpower ?? []), ...extras(r, 'manpower'),
    ...narrative(r, 'Survey', 'survey'), ...extras(r, 'survey'),
    ...narrative(r, 'Safety', 'safety'), ...extras(r, 'safety'),
    ...(internalOnly.equipment ?? []), ...extras(r, 'equipment'),
    ...narrative(r, 'Logistics', 'logistics'), ...extras(r, 'logistics'),
  ];
}

export function buildInternalReportDocx(r: InternalWeeklyReport, photos: Map<string, EmbeddedPhoto>, logo: EmbeddedPhoto | null): Document {
  return assemble(r, FOOTER_NOTE.internal, body(r, photos, { manpower: manpower(r), equipment: equipment(r) }), logo);
}

export function buildClientReportDocx(r: ClientWeeklyReport, photos: Map<string, EmbeddedPhoto>, logo: EmbeddedPhoto | null): Document {
  return assemble(r, FOOTER_NOTE.client, body(r, photos, {}), logo);
}

/** Hand a built document to the browser as a download. */
export async function downloadDocx(doc: Document, filename: string): Promise<void> {
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoked on the next tick: Safari aborts the download if the object URL
  // disappears in the same frame as the click (same as the training export).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
